import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

// Editor kompletního zápisu (per hod/frame) jako overlay u buňky. Skóre se dopočítává live přes
// Scoring. Vstup přijímá čísla i znaky: X = 10 (strike), / = spare (dopočítá zbytek do 10),
// - = 0. Split se značí příponou "s" (např. 7s nebo /s). Po striku v 1. políčku se 2. políčko
// rámce (1-9) zneplatní; v 10. rámci se 3. políčko ukáže jen při dohození (strike/spare).

private class ParsedCell(val empty: Boolean, val spare: Boolean, val pins: Int, val split: Boolean)

private fun parseCell(raw: String): ParsedCell {
    var s = raw.trim().lowercase()
    val split = s.endsWith("s")
    if (split) s = s.dropLast(1).trim()
    return when {
        s.isEmpty() -> ParsedCell(empty = true, spare = false, pins = 0, split = split)
        s == "x" -> ParsedCell(false, false, 10, split)
        s == "-" -> ParsedCell(false, false, 0, split)
        s == "/" -> ParsedCell(false, spare = true, pins = 0, split)
        else -> {
            val n = s.toIntOrNull()
            if (n != null && n in 0..10) ParsedCell(false, false, n, split) else ParsedCell(true, false, 0, split)
        }
    }
}

/** Efektivní počet kuželek políčka; `prev` = 1. hod rámce (kvůli "/"). null = prázdné/nevalidní. */
private fun cellPins(c: ParsedCell?, prev: Int?): Int? = when {
    c == null || c.empty -> null
    c.spare -> if (prev != null) 10 - prev else null
    else -> c.pins
}

/** Kontrola, že žádný rack rámce nemá součet > 10 (nelze zadat např. 7 a 5). "/" a "-" jsou vždy OK. */
private fun frameRacksOk(type: String, cells: List<ParsedCell>, f: Int): Boolean {
    val p1 = cellPins(cells[0], null) ?: return true
    fun pairOk(a: Int, c: ParsedCell?): Boolean =
        c == null || c.empty || c.spare || a + c.pins <= 10
    if (f < 9) {
        if (Scoring.isStrike(type, p1)) return true // 2. políčko se ignoruje
        return pairOk(p1, cells.getOrNull(1))
    }
    // 10. rámec
    if (!Scoring.isStrike(type, p1)) return pairOk(p1, cells.getOrNull(1)) // box3 je bonus (čerstvý rack)
    val p2 = cellPins(cells.getOrNull(1), null) ?: return true             // box1 strike -> box2 čerstvý
    if (Scoring.isStrike(type, p2)) return true                            // box3 čerstvý
    return pairOk(p2, cells.getOrNull(2))                                  // box2+box3 jeden rack
}

private fun validChange(type: String, inputs: Map<String, String>, f: Int, t: Int, nv: String): Boolean {
    val count = if (f < 9) 2 else 3
    val cells = (0 until count).map { if (it == t) parseCell(nv) else parseCell(inputs["$f#$it"] ?: "") }
    return frameRacksOk(type, cells, f)
}

private class FrameInfo(
    val throws: List<Int>,
    val split: Boolean,
    val box2Disabled: Boolean, // rámce 1-9: 1. hod je strike -> 2. políčko neaktivní
    val showThird: Boolean,    // 10. rámec: 3. políčko jen při dohození
)

private fun frameInfo(type: String, inputs: Map<String, String>, f: Int): FrameInfo {
    val count = if (f < 9) 2 else 3
    val cells = (0 until count).map { parseCell(inputs["$f#$it"] ?: "") }
    val split = cells.any { it.split }

    val p1 = cellPins(cells[0], null)
    if (p1 == null) return FrameInfo(emptyList(), split, box2Disabled = false, showThird = false)

    if (f < 9) {
        if (Scoring.isStrike(type, p1)) return FrameInfo(listOf(p1), split, box2Disabled = true, showThird = false)
        val p2 = cellPins(cells.getOrNull(1), p1)
        return FrameInfo(if (p2 == null) listOf(p1) else listOf(p1, p2), split, box2Disabled = false, showThird = false)
    }

    // 10. rámec
    val throws = mutableListOf(p1)
    if (Scoring.isStrike(type, p1)) {
        val p2 = cellPins(cells.getOrNull(1), null) // čerstvý rack
        if (p2 == null) return FrameInfo(throws, split, false, showThird = true)
        throws.add(p2)
        val p3 = if (Scoring.isStrike(type, p2)) cellPins(cells.getOrNull(2), null) else cellPins(cells.getOrNull(2), p2)
        if (p3 != null) throws.add(p3)
        return FrameInfo(throws, split, false, showThird = true)
    } else {
        val p2 = cellPins(cells.getOrNull(1), p1)
        if (p2 == null) return FrameInfo(throws, split, false, showThird = false)
        throws.add(p2)
        val spare = Scoring.isSpare(type, p1, p2)
        if (spare) {
            val p3 = cellPins(cells.getOrNull(2), null)
            if (p3 != null) throws.add(p3)
        }
        return FrameInfo(throws, split, false, showThird = spare)
    }
}

@Composable
fun ScoresheetEditor(
    gameType: String,
    initialFrames: List<FrameDto>,
    onSave: (List<FrameDto>, Int) -> Unit,
    onClose: () -> Unit,
    onDelete: (() -> Unit)? = null,
    photoImportEnabled: Boolean = false,
    gameLabel: String? = null,
    expectedScore: Int? = null,
) {
    var draftFrames by remember { mutableStateOf(initialFrames) }
    var revision by remember { mutableStateOf(0) }
    var imported by remember { mutableStateOf(false) }
    var photoScore by remember { mutableStateOf<Int?>(null) }
    Div(attrs = { classes("sheet-overlay"); onClick { onClose() } }) {
        Div(attrs = { classes("sheet-panel"); onClick { it.stopPropagation() } }) {
            Div(attrs = { classes("sheet-panel-head") }) {
                H3 { Text("Kompletní zápis — ${bowlingTypeLabel(gameType)}${gameLabel?.let { " · $it" } ?: ""}") }
                Button(attrs = { classes("back-button"); onClick { onClose() } }) { Text("✕") }
            }
            if (photoImportEnabled) PhotoImport(emptyList(), remember { mutableStateMapOf<String, List<FrameDto>>() },
                typeForGame = { gameType }, onSelectGame = { frames, total ->
                    draftFrames = frames; photoScore = total; imported = true; revision++
                })
            key(revision) {
            ScoresheetFields(gameType, draftFrames, onChange = { frames, _ -> draftFrames = frames }, highlightIncomplete = imported, footer = { frames, total ->
                if (imported) {
                    P { Text("Skóre na fotce: ${photoScore ?: "nečitelné"}") }
                    if (photoScore != null && photoScore != total) P(attrs = { classes("form-note") }) { Text("Součet hodů nesouhlasí se skóre na fotce. Zkontroluj zápis.") }
                    photoFramesError(gameType, frames)?.let { P(attrs = { classes("form-note") }) { Text(it) } }
                }
                if (expectedScore != null && expectedScore != total) P(attrs = { classes("form-note") }) {
                    Text("Skóre vybrané hry ($expectedScore) nesouhlasí se součtem hodů ($total).")
                }
                Div(attrs = { classes("form-actions") }) {
                    Button(attrs = { classes("primary-btn"); if (imported && photoFramesError(gameType, frames) != null) disabled(); onClick { onSave(frames, total) } }) { Text("Uložit zápis") }
                    if (onDelete != null && initialFrames.isNotEmpty()) {
                        Button(attrs = { classes("small-btn", "danger-btn"); onClick { onDelete() } }) { Text("Smazat zápis") }
                    }
                    Button(attrs = { classes("small-btn"); onClick { onClose() } }) { Text("Zrušit") }
                }
            })
            }
        }
    }
}

/** Shared editable fields, also used directly in the photo-import review. */
@Composable
fun ScoresheetFields(
    gameType: String,
    initialFrames: List<FrameDto>,
    onChange: ((List<FrameDto>, Int) -> Unit)? = null,
    highlightIncomplete: Boolean = false,
    footer: @Composable (List<FrameDto>, Int) -> Unit = { _, _ -> },
) {
    val inputs = remember {
        mutableStateMapOf<String, String>().apply {
            initialFrames.forEachIndexed { f, fr ->
                val marks = Scoring.frameMarks(gameType, fr.t, f == 9)
                fr.t.forEachIndexed { t, pins ->
                    val text = if (gameType != "narubak" && marks.getOrNull(t) == "/") "/"
                        else if (pins == 10) "X" else if (pins == 0) "-" else pins.toString()
                    put("$f#$t", if (fr.split && t == fr.t.lastIndex) "${text}s" else text)
                }
            }
        }
    }

    // revize: zvýšení vynutí překreslení inputů (zahodí neplatný vstup a vrátí DOM na stav)
    var rev by remember { mutableStateOf(0) }

    val infos = (0 until 10).map { frameInfo(gameType, inputs, it) }
    val frames = infos.map { FrameDto(it.throws, it.split) }
    val total = Scoring.total(gameType, frames.map { it.t })
    val cumulative = Scoring.resolvedFrameCumulative(gameType, frames.map { it.t })

    Div(attrs = { classes("scoresheet-fields") }) {
            P(attrs = { classes("muted") }) { Text("X = 10, / = spare, - = 0. Split příponou s (např. 7s).") }

            Div(attrs = { classes("frames-row") }) {
                for (f in 0 until 10) {
                    val info = infos[f]
                    val boxes = if (f < 9) 2 else 3
                    val strike = info.throws.firstOrNull()?.let { Scoring.isStrike(gameType, it) } == true
                    val required = if (f == 9 && info.showThird) 3 else if (strike) 1 else 2
                    val incomplete = highlightIncomplete && info.throws.size != required
                    Div(attrs = {
                        classes(*(listOfNotNull("frame-box", "frame-split".takeIf { info.split }, "frame-unread".takeIf { incomplete }).toTypedArray()))
                        if (incomplete) attr("title", "Frame není úplně načtený. Doplň chybějící hody.")
                    }) {
                        Div(attrs = { classes("frame-no") }) { Text("${f + 1}") }
                        if (incomplete) Span(attrs = { classes("frame-unread-label") }) { Text("Doplnit") }
                        Div(attrs = { classes("frame-throws") }) {
                            for (t in 0 until boxes) {
                                // 10. rámec: 3. políčko jen při dohození
                                if (f == 9 && t == 2 && !info.showThird) {
                                    Div(attrs = { classes("frame-input", "frame-input-hidden") }) {}
                                } else {
                                    val disabled = f < 9 && t == 1 && info.box2Disabled
                                    key(rev, f, t) {
                                        Input(type = InputType.Text) {
                                            classes(*(listOfNotNull("frame-input", "frame-input-disabled".takeIf { disabled }).toTypedArray()))
                                            attr("maxlength", "3")
                                            attr("aria-label", "Frame ${f + 1}, hod ${t + 1}")
                                            if (incomplete && !disabled) attr("aria-invalid", "true")
                                            if (disabled) { attr("readonly", "readonly"); attr("tabindex", "-1") }
                                            value(if (disabled) "" else inputs["$f#$t"] ?: "")
                                            onInput {
                                                if (!disabled) {
                                                    val nv = it.value
                                                    if (validChange(gameType, inputs, f, t, nv)) {
                                                        inputs["$f#$t"] = nv
                                                        val current = frameInfo(gameType, inputs, f)
                                                        if (gameType != "narubak" && Scoring.frameMarks(gameType, current.throws, f == 9).getOrNull(t) == "/") {
                                                            inputs["$f#$t"] = if (parseCell(nv).split) "/s" else "/"
                                                        }
                                                        val updated = (0 until 10).map { frameInfo(gameType, inputs, it) }
                                                            .map { FrameDto(it.throws, it.split) }
                                                        onChange?.invoke(updated, Scoring.total(gameType, updated.map { it.t }))
                                                    } else rev++
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        Div(attrs = { classes("frame-cumulative") }) { Text(cumulative[f]?.toString() ?: "–") }
                    }
                }
            }

            Div(attrs = { classes("sheet-total") }) {
                Span(attrs = { classes("muted") }) { Text("Součet") }
                Span(attrs = { classes("sheet-total-value") }) { Text("$total") }
            }

            footer(frames, total)
    }
}

/** Read-only vykreslení kompletního zápisu (10 rámců, značky + kumulativní součty). */
@Composable
fun ScoresheetStrip(gameType: String, frames: List<FrameDto>) {
    if (frames.isEmpty()) return
    val cum = Scoring.frameCumulative(gameType, frames.map { it.t })
    Div(attrs = { classes("sheet-strip") }) {
        frames.forEachIndexed { idx, fr ->
            val marks = Scoring.frameMarks(gameType, fr.t, idx == 9)
            Div(attrs = { classes("strip-frame") }) {
                Div(attrs = { classes("strip-no") }) { Text("${idx + 1}") }
                Div(attrs = { classes("strip-marks") }) {
                    marks.forEachIndexed { j, m ->
                        Span(attrs = { classes(*(listOfNotNull("strip-cell", "strip-split-mark".takeIf { fr.split && j == 0 }).toTypedArray())) }) { Text(m) }
                    }
                }
                Div(attrs = { classes("strip-cum") }) { Text("${cum.getOrElse(idx) { 0 }}") }
            }
        }
    }
}
